import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { HiArrowNarrowRight, HiOutlineExternalLink } from "react-icons/hi";
import { FaGithub } from "react-icons/fa";
import { data } from "../../data/data.js";

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const project = data.find((item) => item.slug === slug);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    if (!project) return undefined;
    const previous = document.title;
    document.title = `${project.name} case study - Aaron Bryant`;
    return () => {
      document.title = previous;
    };
  }, [project]);

  if (!project) {
    return <Navigate to="/case-studies" replace />;
  }

  const photos = project.screenshots ?? [];
  const currentIndex = data.findIndex((item) => item.slug === slug);
  const nextProject = data[(currentIndex + 1) % data.length];

  return (
    <article className="px-6 py-12 sm:px-8 md:py-20">
      <div className="mx-auto max-w-4xl">
        <nav
          className="mb-8 flex flex-wrap items-center gap-2 text-sm text-[var(--color-muted)]"
          aria-label="Breadcrumb"
        >
          <Link to="/" className="classic-link">
            Aaron Bryant
          </Link>
          <span aria-hidden="true">/</span>
          <Link to="/case-studies" className="classic-link">
            Case Studies
          </Link>
          <span aria-hidden="true">/</span>
          <span className="text-[var(--color-text)]">{project.name}</span>
        </nav>

        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-accent)]">
          {project.category}
        </p>
        <h1 className="mt-3 text-4xl font-medium tracking-tight sm:text-5xl">
          {project.name}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-[var(--color-muted)]">
          {project.description}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <span key={tag} className="classic-tag">
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              View live site
              <HiOutlineExternalLink />
            </a>
          )}
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary gap-2"
            >
              <FaGithub />
              View on GitHub
            </a>
          )}
          {!project.url && !project.github && (
            <span className="inline-flex items-center rounded-md border border-[var(--color-rule)] px-4 py-2 text-sm font-medium text-[var(--color-muted)]">
              {project.status ?? "Site offline"}
            </span>
          )}
        </div>

        {photos.length > 0 && (
          <div className="mt-12">
            <div className="shot-frame">
              <img
                src={photos[photoIndex].src}
                alt={photos[photoIndex].alt}
                className="max-h-[480px] w-full object-contain object-top"
              />
            </div>
            {photos.length > 1 && (
              <>
                <p className="mt-3 text-center text-sm text-[var(--color-muted)]">
                  {photoIndex + 1} / {photos.length} - {photos[photoIndex].alt}
                </p>
                <div
                  className="mt-4 flex justify-center gap-2 overflow-x-auto pb-1"
                  role="tablist"
                  aria-label="Project screenshots"
                >
                  {photos.map((shot, index) => (
                    <button
                      key={shot.alt}
                      type="button"
                      role="tab"
                      aria-selected={index === photoIndex}
                      aria-label={shot.alt}
                      onClick={() => setPhotoIndex(index)}
                      className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded-md border-2 transition-opacity duration-200 ${
                        index === photoIndex
                          ? "border-[var(--color-accent)]"
                          : "border-[var(--color-rule)] opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={shot.src}
                        alt=""
                        className="h-full w-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {project.story && (
          <section className="mt-12 grid gap-6 border-t border-[var(--color-rule)] pt-8 sm:grid-cols-3">
            {[
              ["My role", project.story.role],
              ["The problem", project.story.problem],
              ["The result", project.story.result],
            ].map(([label, text]) => (
              <div key={label}>
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">
                  {label}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-[var(--color-muted)]">
                  {text}
                </p>
              </div>
            ))}
          </section>
        )}

        {project.highlights?.length > 0 && (
          <section className="mt-14">
            <h2 className="text-2xl font-medium">Highlights</h2>
            <ul className="mt-5 space-y-3">
              {project.highlights.map((item) => (
                <li
                  key={item}
                  className="flex gap-3 text-base leading-relaxed text-[var(--color-muted)]"
                >
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--color-accent)]" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-16 rounded-lg border border-[var(--color-rule)] bg-[var(--color-surface)] p-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-muted)]">
            Next
          </p>
          <Link
            to={`/case-studies/${nextProject.slug}`}
            className="group mt-3 flex items-center justify-between gap-4"
          >
            <div>
              <h3 className="text-xl font-medium group-hover:text-[var(--color-accent)]">
                {nextProject.name}
              </h3>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                {nextProject.category}
              </p>
            </div>
            <HiArrowNarrowRight className="text-xl transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default CaseStudyDetailPage;
